import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { chatService } from '../services/chat-service';
import { authService } from '../services/auth-service';
import { moduleService } from '../services/module-service';
import { AppHeader } from '../components/AppHeader';
import { Breadcrumb } from '../components/Breadcrumb';
import { GradeBadge } from '../components/GradeBadge';
import { InlineList, InlineListRow } from '../components/InlineList';
import type { SubjectListResponse } from '../types/chat';
import type { User } from '../types/auth';
import './Subjects.css';
import '../styles/common.css';


const SubjectLastScoresTable = ({ subjects }: { subjects: SubjectListResponse[] }) => (
  <div className="subject-scores-table-wrapper">
    <table className="subject-scores-table">
      <thead>
        <tr>
          <th scope="col"></th>
          {subjects.map(subject => <th key={subject.id} scope="col">{subject.number}</th>)}
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">Last</th>
          {subjects.map(subject => {
            const lastGrade = [...(subject.conversations ?? [])]
              .filter(c => !c.isAborted)
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .find(c => c.grade !== undefined)?.grade;
            return <td key={subject.id}>{lastGrade !== undefined && <GradeBadge grade={lastGrade} />}</td>;
          })}
        </tr>
      </tbody>
    </table>
  </div>
);


function Subjects() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subjects, setSubjects] = useState<SubjectListResponse[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSubject, setExpandedSubject] = useState<string | null>(null);
  const [moduleInfo, setModuleInfo] = useState<{ name?: string; className?: string }>({});

  const handleSubjectToggle = (subjectId: string) => {
    setExpandedSubject(expandedSubject === subjectId ? null : subjectId);
  };

  const handleSubjectClick = (subject: SubjectListResponse) => {
    sessionStorage.setItem('currentSubjectNumber', subject.number);
    sessionStorage.setItem('currentSubjectTitle', subject.title);
    navigate(`/conversation/new/${subject.id}`);
  };

  const handleConversationClick = (conversationId: string, subject: SubjectListResponse) => {
    sessionStorage.setItem('currentSubjectNumber', subject.number);
    sessionStorage.setItem('currentSubjectTitle', subject.title);
    navigate(`/conversation/${conversationId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = async () => {
    try {
      // Zuerst alle offenen Konversationen abbrechen
      await chatService.cancelAllConversations();
    } catch (error) {
      // Trotzdem fortfahren mit Logout, auch wenn Abbrechen fehlschlägt
    }
    
    // Dann normal ausloggen
    authService.logout();
    navigate('/');
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        // Check if user is authenticated
        if (!authService.isAuthenticated()) {
          navigate('/');
          return;
        }

        // Get current user
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);

        const moduleId = searchParams.get('moduleId');
        const classId = searchParams.get('classId');

        if (!moduleId) {
          setError('No module selected. Please select a module from the list.');
          return;
        }

        sessionStorage.setItem('currentModuleId', moduleId);
        if (classId) {
          sessionStorage.setItem('currentClassId', classId);
        } else {
          sessionStorage.removeItem('currentClassId');
        }

        // Load module details for the page title
        const modules = await moduleService.getModules();
        const currentModule = modules.find(m => m.id === moduleId && (!classId || m.classId === classId));
        if (currentModule) {
          setModuleInfo({ name: currentModule.name, className: currentModule.className });
        }

        // Fetch subjects for the selected module (and class, if the module was assigned via one)
        const subjectsData = await chatService.getSubjectList(moduleId, classId ?? undefined);
        setSubjects(subjectsData);
        setError(null);
      } catch (err: any) {
        if (err.response?.status === 401) {
          // Token expired or invalid, logout and redirect
          authService.logout();
          navigate('/');
        } else {
          setError('Could not load data. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="subjects-container">
        <AppHeader
          user={user}
          title="KI Expert"
          subtitle="Select a subject from the list:"
        />
        <div className="loading">Loading data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subjects-container">
        <AppHeader
          user={user}
          title="KI Expert"
          subtitle="Select a subject from the list:"
        />
        <div className="error">{error}</div>
        <button onClick={handleLogout} className="logout-button">
          Back to login
        </button>
      </div>
    );
  }

  return (
    <div className="subjects-container">
      <AppHeader 
        user={user} 
        title="KI Expert" 
        subtitle="Select a subject from the list:" 
      />
      
      <main className="list-main">
        <div className="navigation-bar">
          <div className="navigation-bar-header">
            <Breadcrumb items={[
              { label: 'Modules', path: '/modules' },
              { label: 'Subjects' },
            ]} />
            <h2 className="navigation-bar-title">
              {moduleInfo.name
                ? moduleInfo.className
                  ? `Subjects - ${moduleInfo.name} - ${moduleInfo.className}`
                  : `Subjects - ${moduleInfo.name}`
                : 'Subjects'}
            </h2>
          </div>
        </div>
        {subjects.length > 0 ? (
          <>
          <SubjectLastScoresTable subjects={subjects} />
          <InlineList>
            {subjects.map((subject) => (
              <div key={subject.id} className={`subject-block ${subject.status === 2 ? 'completed' : ''}`}>
                <InlineListRow
                  onClick={() => handleSubjectToggle(subject.id)}
                  actions={(() => {
                    const lastConversation = subject.conversations && subject.conversations.length > 0
                      ? [...subject.conversations]
                          .filter(c => !c.isAborted)
                          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
                      : null;
                    if (lastConversation && lastConversation.isCompleted && lastConversation.grade !== undefined) {
                      return <GradeBadge grade={lastConversation.grade} />;
                    }
                    return <span className="status-badge user-status-in-progress">In progress</span>;
                  })()}
                >
                  <span className="subject-number">{subject.number}</span>
                  <h3 className="subject-title">{subject.title}</h3>
                </InlineListRow>

                {/* Konversationsliste unterhalb des Subjects */}
                {expandedSubject === subject.id && (
                  <div className="subject-conversations">
                    {/* Bestehende Konversationen */}
                    {subject.conversations && subject.conversations.length > 0 && (
                      [...subject.conversations]
                        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                        .map((conversation) => (
                        <div 
                          key={conversation.id}
                          className={`user-conversation-item ${conversation.isAborted ? 'aborted' : (conversation.isCompleted ? 'completed' : 'open')}`}
                          onClick={() => !conversation.isAborted && handleConversationClick(conversation.id, subject)}
                          role={conversation.isAborted ? undefined : 'button'}
                          tabIndex={conversation.isAborted ? undefined : 0}
                        >
                          <div className="user-conversation-info">
                            <span className="user-conversation-date">
                              {formatDate(conversation.createdAt)}
                            </span>
                            <span className="user-conversation-messages">
                              {conversation.messageCount} {conversation.messageCount === 1 ? 'answer' : 'answers'}
                            </span>
                          </div>
                          <div className="user-conversation-status">
                            {conversation.isAborted ? (
                              <span className="user-status-badge small aborted">
                                Aborted
                              </span>
                            ) : conversation.isCompleted && conversation.grade !== undefined ? (
                              <GradeBadge grade={conversation.grade} />
                            ) : (
                              <span className="user-status-badge small open">
                                Open
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}

                    {/* "Neue Konversation" Option - nur wenn keine offene Konversation existiert und nicht am gleichen Tag */}
                    {(() => {
                      const hasOpenConversation = subject.conversations?.some(c => !c.isCompleted && !c.isAborted);
                      const today = new Date().toDateString();
                      const hasConversationToday = subject.conversations?.some(c => 
                        new Date(c.createdAt).toDateString() === today
                      );
                      return !hasOpenConversation && !hasConversationToday;
                    })() && (
                      <div 
                        className="user-conversation-item new"
                        onClick={() => handleSubjectClick(subject)}
                      >
                        <div className="user-conversation-info">
                          <span className="user-conversation-new">
                            + Start new test
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Blockierte "Neue Konversation" - wenn heute bereits eine existiert (abgeschlossen oder abgebrochen) */}
                    {(() => {
                      const hasOpenConversation = subject.conversations?.some(c => !c.isCompleted && !c.isAborted);
                      const today = new Date().toDateString();
                      const hasConversationToday = subject.conversations?.some(c => 
                        new Date(c.createdAt).toDateString() === today
                      );
                      return !hasOpenConversation && hasConversationToday;
                    })() && (
                      <div className="user-conversation-item new blocked">
                        <div className="user-conversation-info">
                          <span className="user-conversation-new blocked">
                            + Start new test
                          </span>
                          <span className="user-block-message">
                            (Only one test per day allowed)
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </InlineList>
          </>
        ) : (
          <p>No subjects available</p>
        )}
      </main>
    </div>
  );
}

export default Subjects;
